import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PaginatePipeArgs } from 'ngx-pagination';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { ApiTransaction, ApiTransactionTypes, ApiUiTransactionTypes, AvailablePeriodsEnum, PaginatedTransactionInput, SelectItemCheckbox, TransactionTypesEnum } from 'src/app/services/rest-api/rest-api';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { SelectItem, SelectItemTyped } from 'src/app/shared/dropdown/dropdown';
import { defaultSelectedValue } from 'src/app/shared/dropdown/dropdown.component';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';

interface FiltrationFormGroup {
  checkboxes: FormArray<FormControl<SelectItemCheckbox>>,
  periods: FormControl<SelectItem | null>,
  investment: FormControl<string  | null | undefined>,
}

export interface TransactionsPreloaders {
  transactions: boolean,
  investments: boolean,
}

@Component({
  selector: 'libero-filterable-transactions',
  templateUrl: './filterable-transactions.component.html',
  styleUrl: './filterable-transactions.component.scss',
  standalone: false
})
export class FilterableTransactionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public preloaders!: TransactionsPreloaders;
  @Input() public transactions!: ApiTransaction[];
  @Input() public availableInvestments!: SelectItemTyped<number>[];
  @Input() public initialInvestmentId?: string;
  @Input() public totalItems?: number;

  @Output() public dataUpdated = new EventEmitter<PaginatedTransactionInput>();

  public initialInvestment?: SelectItemTyped<number>;

  public paginationConfig: PaginatePipeArgs = {
    itemsPerPage: 10,
    currentPage: 1,
    id: 'transactions-pagination',
    totalItems: this.totalItems,
  };

  public unknownTypeIconPath = 'assets/icons/question-mark.svg';
  public mappedTransactionIcons: Record<string, string | undefined> = {
    'MONEY_DEBIT': '/assets/icons/transactions/withdraw.svg',
    'ORDER_DEBIT': '/assets/icons/transactions/sale.svg',
    'MONEY_CREDIT': '/assets/icons/transactions/deposit.svg',
    'ORDER_CREDIT': '/assets/icons/transactions/purchase.svg',
    'FEE_DEBIT': '/assets/icons/transactions/fee.svg',
    'FEE_CREDIT': '/assets/icons/transactions/fee.svg',
    'OPF_MANAGEMENT_FEE_REFUND_CREDIT': '/assets/icons/transactions/deposit.svg',
    'DIVIDEND_CREDIT': '/assets/icons/transactions/deposit.svg',
    'FX_TRANSACTION_CREDIT': '/assets/icons/transactions/purchase.svg',
    'FX_TRANSACTION_DEBIT': '/assets/icons/transactions/sale.svg',
    'ROUNDING_RETURN_CREDIT': '/assets/icons/transactions/deposit.svg',
  }

  private get filtrationCheckboxes(): UntypedFormArray {
    return this.filtrationForm.controls['checkboxes'] as UntypedFormArray;
  }

  public get showMarkAllBtn(): boolean {
    return this.filtrationCheckboxes.value.filter((box: SelectItem) => box.value).length !== this.filtrationCheckboxes.value.length;
  }

  public availablePeriods: SelectItem[] = [
    {
      label: 'availableTransactionPeriods.last30Days',
      value: AvailablePeriodsEnum.D30,
      translate: true,
    },
    {
      label: 'availableTransactionPeriods.last3Months',
      value: AvailablePeriodsEnum.M3,
      translate: true,
    },
    {
      label: 'availableTransactionPeriods.last6Months',
      value: AvailablePeriodsEnum.M6,
      translate: true,
    },
    {
      label: 'availableTransactionPeriods.YTD',
      value: AvailablePeriodsEnum.YTD,
      translate: true,
    },
    {
      label: 'availableTransactionPeriods.lastYear',
      value: AvailablePeriodsEnum.Y1,
      translate: true,
    },
    {
      label: 'availableTransactionPeriods.last5Years',
      value: AvailablePeriodsEnum.Y5,
      translate: true,
    },
    {
      label: 'availableTransactionPeriods.all',
      value: AvailablePeriodsEnum.ALL,
      translate: true,
    },
  ];

  public get allPeriodsItem(): SelectItem {
    return this.availablePeriods.find(period => period.value === AvailablePeriodsEnum.ALL)!;
  }

  private formSubscriptions = new Subscription();

  private availableTypes: SelectItemCheckbox[] = [
    {
      label: this.translate.instant('money.transactionTypes.deposits'),
      apiKey: ApiUiTransactionTypes.DEPOSIT,
      value: true,
    },
    {
      label: this.translate.instant('money.transactionTypes.withdraws'),
      apiKey: ApiUiTransactionTypes.WITHDRAWAL,
      value: true
    },
    {
      label: this.translate.instant('money.transactionTypes.purchases'),
      apiKey: ApiUiTransactionTypes.BUY,
      value: true
    },
    {
      label: this.translate.instant('money.transactionTypes.sells'),
      apiKey: ApiUiTransactionTypes.SELL,
      value: true
    },
    {
      label: this.translate.instant('money.transactionTypes.fees'),
      apiKey: ApiTransactionTypes.FEE,
      value: false
    },
    {
      label: this.translate.instant('money.transactionTypes.refunds'),
      apiKey: ApiTransactionTypes.OPF_MANAGEMENT_FEE_REFUND,
      value: false
    },
    {
      label: this.translate.instant('money.transactionTypes.dividends'),
      apiKey: ApiUiTransactionTypes.DIVIDEND,
      value: false
    },
    {
      label: this.translate.instant('money.transactionTypes.fx'),
      apiKey: ApiUiTransactionTypes.FX_TRANSACTION,
      value: false
    },
  ];

  public filtrationForm!: FormGroup;

  public constructor(
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    public loggerService: LoggerService,
    private toastService: ToastNotificationService,
    private router: Router,
    private modalService: ModalService,
  ) {}

  public ngOnInit(): void {
    this.initForm();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['totalItems']) {
      this.paginationConfig.totalItems = changes['totalItems'].currentValue;
    } else if (changes['availableInvestments'] && this.initialInvestmentId) {

      this.initialInvestment = this.availableInvestments.find(
        investment => this.initialInvestmentId ? investment.value === parseInt(this.initialInvestmentId) : false
      );
    }
  }

  private initForm(): void {
    const showAllPeriod = this.allPeriodsItem;

    this.filtrationForm = new FormGroup<FiltrationFormGroup>({
      checkboxes: this.generateFormArray(this.availableTypes),
      periods: new FormControl(showAllPeriod),
      investment: new FormControl(this.initialInvestmentId),
    });

    this.paginationConfig.currentPage = 1;

    this.formSubscriptions.add(
      this.filtrationForm.get('checkboxes')!.valueChanges.pipe(
        startWith(null), // trigger init fetch
        debounceTime(150),
        distinctUntilChanged(),
      ).subscribe(() => {
        this.updateData()
      })
    );
  }

  private generateFormArray(controlsData: SelectItemCheckbox[]): UntypedFormArray {
    const formArray = new UntypedFormArray([]);

    for (const checkbox of controlsData) {
      formArray.push(this.createCheckboxControl(checkbox));
    }

    return formArray;
  }

  private createCheckboxControl(checkbox: SelectItemCheckbox): AbstractControl {
    return this.fb.group({
      name: checkbox.label,
      apiKey: checkbox.apiKey,
      value: checkbox.value
    });
  }

  public updateCheckboxAndData(): void {
    this.paginationConfig.currentPage = 1;
    this.updateData();
  }

  public prevPageAvailable(): boolean {
    const currentPageNumber = this.paginationConfig.currentPage ? +this.paginationConfig.currentPage : 0;
    return currentPageNumber > 1;
  }

  public nextPageAvailable(): boolean {
    const moreItemsAvailable = (
      (this.paginationConfig.totalItems as number) -
      (((this.paginationConfig.currentPage as number)) * (this.paginationConfig.itemsPerPage as number))
    ) > 0;
    return moreItemsAvailable;
  }

  public loadPrevPage(): void {
    this.paginationConfig.currentPage = (this.paginationConfig.currentPage as number) - 1;
    this.updateData();
  }

  public loadNextPage(): void {
    this.paginationConfig.currentPage = (this.paginationConfig.currentPage as number) + 1;
    this.updateData();
  }

  public goToPage(page: number): void {
    this.paginationConfig.currentPage = page;
    this.updateData();
  }

  private createPaginatedOptions(): PaginatedTransactionInput {
    const options: PaginatedTransactionInput = {
      size: this.paginationConfig.itemsPerPage as number,
      page: this.paginationConfig.currentPage as number,
    };

    if (
      this.filtrationForm.value['investment']?.value &&
      this.filtrationForm.value['investment'].value !== 'show-all-available-data'
    ) {
      options.portfolioId = this.filtrationForm.value['investment'].value;
    }

    if (this.filtrationForm.value['periods']?.value && this.createTransactionPeriod()) {
      options.transactionTimestampTo = new Date().toISOString(); // always to this moment
      options.transactionTimestampFrom = this.createTransactionPeriod();
    }

    if (this.someFiltrationTypeUnchecked()) {
      options.type = this.getCheckedTransactionTypes();
    }

    return options;
  }

  public get availableFilteredTransactions(): boolean {
    if (!this.filtrationForm) {
      return false;
    }

    const selectedFiltrationCheckboxes = (this.filtrationForm.value['checkboxes'] as SelectItem[]).filter(checkbox => checkbox.value).length;

    return !!this.transactions.length && selectedFiltrationCheckboxes !== 0;
  }


  private someFiltrationTypeUnchecked(): boolean {
    const checkboxes = this.filtrationForm.value['checkboxes'] as SelectItem[];
    return !!checkboxes?.filter(checkbox => !checkbox.value).length;
  }

  private getCheckedTransactionTypes(): Record<number, keyof typeof TransactionTypesEnum | keyof typeof ApiTransactionTypes> {
    const types: (keyof typeof TransactionTypesEnum | keyof typeof ApiTransactionTypes)[] = [];
    const checkboxes = this.filtrationForm.value['checkboxes'];

    for (const checkbox of checkboxes) {
      if (checkbox.value) {
        types.push(checkbox.apiKey);
        // ROUNDING_RETURN is same checkbox filter/category as REFUND
        if(checkbox.value && checkbox.apiKey === ApiTransactionTypes.OPF_MANAGEMENT_FEE_REFUND) {
          types.push(ApiTransactionTypes.ROUNDING_RETURN);
        }
      }
    }

    return types;
  }

  private createTransactionPeriod(): string {
    const selectedTerm = this.filtrationForm.value['periods']?.value as AvailablePeriodsEnum;
    const today = new Date();

    switch (selectedTerm) {
      case AvailablePeriodsEnum.D30:
        return new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      case AvailablePeriodsEnum.M3:
        today.setMonth(today.getMonth() - 3);
        return today.toISOString();
      case AvailablePeriodsEnum.M6:
        today.setMonth(today.getMonth() - 6);
        return today.toISOString();
      case AvailablePeriodsEnum.YTD:
        return new Date(today.getFullYear(), 0, 1).toISOString();
      case AvailablePeriodsEnum.Y1:
        today.setFullYear(today.getFullYear() - 1);
        return today.toISOString();
      case AvailablePeriodsEnum.Y5:
        today.setFullYear(today.getFullYear() - 5);
        return today.toISOString();
      case defaultSelectedValue.value:
        return '';
      case 'ALL':
        return '';
      default:
        this.loggerService.error('Unknown term inside createTransactionPeriod: ', selectedTerm);
        return today.toISOString();
    }
  }

  public markAll(mark = true): void {
    for (const checkboxControl of this.filtrationCheckboxes.controls) {
      checkboxControl.setValue({
        value: mark,
        name: checkboxControl.value.name,
        apiKey: checkboxControl.value.apiKey,
      });
    }

    this.paginationConfig.currentPage = 1;
  }

  public valueUpdated(newValue: SelectItem | null, type: 'portfolio' | 'term'): void {
    this.paginationConfig.currentPage = 1;

    if(type === 'term') {
      this.filtrationForm.patchValue({ periods: newValue });
    } else if (type === 'portfolio') {
      this.filtrationForm.patchValue({ investment: newValue });
    }

    this.updateData();
  }

  private updateData(): void {
    const newOptions: PaginatedTransactionInput = this.createPaginatedOptions();
    this.filtrationForm.markAllAsTouched();
    this.dataUpdated.emit(newOptions);
  }

  public ngOnDestroy(): void {
    this.formSubscriptions.unsubscribe();
  }

  public redirectToPortfolio(transaction: ApiTransaction): void {
    if (!transaction.portfolio.accessible) {
      this.showPortfolioNotAccessibleWarning();
      return;
    }

    this.router.navigate(['/investment-detail/' + transaction.portfolio.portfolioId]);
  }

  private showPortfolioNotAccessibleWarning(): void {
    this.toastService.addNotification({
      message: 'toastNotifications.messages.portfolioNotAccessible',
      title: 'toastNotifications.headers.error',
      type: 'warning',
      component: ToastNotificationComponent
    });
  }

  public openExportModal(): void {
    this.modalService.openTransactionsExportModal();
  }
}
